import { Box, HStack, Link, VStack } from "@chakra-ui/react";
import FeeFormSectionParts from "features/fees/form/FeeFormSectionParts";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import { NamedDateData } from "gen/model/namedDateData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import {
  FieldValue,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";

export interface AddFeesCalculationFormItemProps {
  index: number;
  namedDates: NamedDateData[];
  onRemove: () => void;
  isLast: boolean;
  isOverride?: boolean;
}

export const FeeFormSectionCalculationItem: React.FC<
  AddFeesCalculationFormItemProps
> = ({ index, namedDates, onRemove, isLast, isOverride }) => {
  const { t } = useTranslation(["fees", "common"]);
  const { control } = useFormContext<FeeCreateAPIDataRequest>();

  return (
    <Box
      bg="gray.25"
      my={4}
      rounded="lg"
      padding={4}
      border={1}
      borderStyle="dashed"
      borderColor="gray.300"
      _last={{
        mb: 0,
      }}
    >
      <VStack alignItems="stretch">
        <HStack w="full" flex={1} alignItems="flex-start" spacing={4}>
          <NamedDateSelect
            namedDateFieldName={`fee_calculations.${index}.named_start_date_id`}
            customDateFieldName={`fee_calculations.${index}.custom_start_date`}
            title={t("fees:namedDates.startDate")}
            isOptional={isLast}
            isDisabled={isOverride}
            namedDates={namedDates}
          />
          <NamedDateSelect
            namedDateFieldName={`fee_calculations.${index}.named_end_date_id`}
            customDateFieldName={`fee_calculations.${index}.custom_end_date`}
            title={t("fees:namedDates.endDate")}
            isOptional={isLast}
            isDisabled={isOverride}
            namedDates={namedDates}
          />
          <Box width="50%">
            <FeeFormSectionParts
              control={control}
              feeCalculationIndex={index}
              feeCalculationRemove={onRemove}
              isOverride={isOverride}
            />
          </Box>
        </HStack>
        {!isOverride && (
          <Restricted permission={PermissionTypeEnum.config}>
            <Box textAlign="end">
              <Link size="sm" onClick={onRemove}>
                {t("fees:feeCalculations.deleteCalculation")}
              </Link>
            </Box>
          </Restricted>
        )}
      </VStack>
    </Box>
  );
};

const NamedDateSelect: React.FC<{
  namedDateFieldName: FieldValue<FieldValues>;
  customDateFieldName: FieldValue<FieldValues>;
  title: string;
  namedDates: NamedDateData[];
  isOptional?: boolean;
  isDisabled?: boolean;
}> = ({
  namedDateFieldName,
  customDateFieldName,
  title,
  namedDates,
  isOptional,
  isDisabled,
}) => {
  const { t } = useTranslation(["common"]);

  const { control } = useFormContext<FeeCreateAPIDataRequest>();
  const value = useWatch({
    control,
    name: namedDateFieldName,
  });

  return (
    <Box width="25%">
      <FormSelect
        name={namedDateFieldName}
        title={title}
        isOptional={isOptional}
        isDisabled={isDisabled}
      >
        <SelectOption value="custom">{t("common:custom.custom")}</SelectOption>
        {namedDates.map((namedDate) => (
          <SelectOption key={namedDate.id} value={namedDate.id!}>
            {namedDate.name}
          </SelectOption>
        ))}
      </FormSelect>
      {value === "custom" && (
        <FormDateInput
          name={customDateFieldName}
          title={t("common:custom.date")}
        />
      )}
    </Box>
  );
};
